// The map's search field: type a place, land on it.
//
// WHY IT IS ON THE MAP AND NOT IN THE HEADER. The header coordinate readout has
// accepted typed positions for months and almost nobody knew, because a readout
// that becomes an input when you click it announces nothing — you have to be told
// it is there. A search field is the one control on a map that everybody already
// expects, in the corner everybody already looks. Discoverability is the whole
// feature; a better parser behind a hidden affordance would have been worth
// nothing.
//
// WHAT IT SEARCHES, AND WHY THAT LIST IS LOCAL. Waypoints — the company's
// standing regions and whatever the crew saved — and the World Port Index, 3,669
// harbours already fetched for the ports layer. Both are held in memory, so
// results appear on the keystroke and keep appearing with the satellite link
// down. A vessel offshore is exactly where somebody needs to find a port and
// exactly where a round trip to a gazetteer fails, so the offline tier is the one
// that had to exist first. Seas, undersea features and EEZs come from Marine
// Regions and are a remote tier still to be added; the shape here leaves room for
// them rather than assuming them.
//
// A POSITION IS ALSO A SEARCH. Anything the coordinate parser recognises — decimal,
// hemisphere letters, degrees-and-minutes — is offered as the first result. That
// makes this field the single answer to "how do I get to a place", whether the
// place has a name or a number.
//
// RANKING, in order: how well the text matches (whole name, then start of name,
// then start of a word inside it, then anywhere), then what kind of thing it is,
// then how far it is from where you are already looking. The last one is not a
// nicety. "Sigsbee Basin" is a real name in the Gulf of Mexico AND off New
// England, 2,000 nm apart; "Cameron" is a fleet base in Louisiana and a harbour in
// several other countries. Whoever is working the Gulf means the Gulf one, and the
// row says which it is so the guess is never silent.

import { parseCoords } from "../ui/coords.js";
import { fmtPosition as fmtCoord } from "../ui/position.js";
import { WAYPOINTS, type Waypoint } from "../ops/waypoints.js";
import { listSaved } from "../ops/savedwaypoints.js";
import { portIndex, type PortPlace } from "./overlays.js";
import { gazetteerSearch, type GazPlace } from "./gazetteer.js";

/** What a result IS, which decides what happens when it is picked. */
export type PlaceKind = "waypoint" | "saved" | "port" | "position" | "sea" | "feature" | "zone";

export interface Place {
  kind: PlaceKind;
  name: string;
  /** The disambiguator: country, note, or the position itself. */
  sub: string;
  lat: number;
  lon: number;
  /** Only a built-in region carries one; a position opens at the current width. */
  spanNm?: number;
}

export interface SearchBox {
  root: HTMLElement;
  destroy(): void;
}

export interface SearchBoxOptions {
  /** Go there. The caller owns what "there" means — the map and the site both
   *  move for a position, because every reading on the console is about the
   *  picked point and a search that moved only the camera would leave the
   *  numbers describing somewhere else. */
  onPick(place: Place): void;
  /** Where the console is looking now, for the proximity tiebreak. Null before
   *  the first point is picked, in which case ranking simply skips it. */
  here(): { lat: number; lon: number } | null;
}

const MAX_ROWS = 8;
const MIN_QUERY = 2;

/** Fold case and strip accents so "Coxen Hole" is found by "coxen" and "Peñón"
 *  by "penon". A search that demands the exact diacritic is a search that fails
 *  on a keyboard the crew does not have. */
function fold(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** Lower is better. -1 means no match at all. */
function matchScore(name: string, q: string): number {
  const n = fold(name);
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  // Start of any word inside the name: "hole" finds "Coxen Hole", which is how
  // people remember harbours they do not spell out in full.
  if (new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(n)) return 2;
  if (n.includes(q)) return 3;
  return -1;
}

/** Kinds in the order they win ties: the crew's own places above the index. */
const KIND_RANK: Record<PlaceKind, number> = {
  position: 0,
  saved: 1,
  waypoint: 2,
  port: 3,
  // The three gazetteer kinds share a rank ON PURPOSE, so distance decides
  // between them rather than an arbitrary preference for zones over features.
  // "Campos" matches a bay in Antarctica and the Campos Basin off Brazil equally
  // well as text; the one 2,000 nm away is the one meant, and ranking seas above
  // features would have put Antarctica first for a fleet working the Atlantic.
  zone: 4,
  sea: 4,
  feature: 4,
};

function roughNm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = aLat - bLat;
  const dLon = (aLon - bLon) * Math.cos((((aLat + bLat) / 2) * Math.PI) / 180);
  return Math.hypot(dLat, dLon) * 60;
}

const KIND_LABEL: Record<PlaceKind, string> = {
  position: "Position",
  saved: "Waypoint",
  waypoint: "Region",
  port: "Port",
  zone: "Country",
  sea: "Sea",
  feature: "Feature",
};

/** How long to wait after the last keystroke before asking the gazetteer. Long
 *  enough that typing "guyana" is one request rather than six, short enough that
 *  it feels like the same act as typing. */
const REMOTE_DEBOUNCE_MS = 280;
const MIN_REMOTE_QUERY = 3;

export function createSearchBox(opts: SearchBoxOptions): SearchBox {
  const root = document.createElement("div");
  root.className = "mapsearch";

  const field = document.createElement("div");
  field.className = "mapsearch-field";
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("viewBox", "0 0 18 18");
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML =
    '<circle cx="8" cy="8" r="5.2" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M11.9 11.9 L16 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>';

  const input = document.createElement("input");
  input.type = "search";
  input.className = "mapsearch-input";
  input.placeholder = "Search a port, waypoint or position";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-autocomplete", "list");

  const clear = document.createElement("button");
  clear.type = "button";
  clear.className = "mapsearch-clear";
  clear.textContent = "×";
  clear.title = "Clear";
  clear.hidden = true;

  field.append(icon, input, clear);

  const list = document.createElement("div");
  list.className = "mapsearch-list";
  list.setAttribute("role", "listbox");
  list.hidden = true;

  root.append(field, list);

  // The port index is fetched on the first real query, not at start-up: most
  // sessions never search, and 3,669 harbours is not worth spending on the
  // chance that this one does. Once fetched it is shared with the ports layer.
  let ports: PortPlace[] | null = null;
  let portsAsked = false;
  const ensurePorts = (): void => {
    if (portsAsked) return;
    portsAsked = true;
    void portIndex().then((p) => {
      ports = p;
      // A result set drawn before the index landed is now incomplete, so redraw
      // it — otherwise the harbour you searched for appears only if you happen
      // to type another character.
      if (input.value.trim().length >= MIN_QUERY) run(input.value);
    });
  };

  // --- the gazetteer tier ---------------------------------------------------
  //
  // Local results are on screen before this is asked, and it folds in when it
  // lands. That ordering is the design: a search that waited ~700 ms for a
  // network round trip before showing the harbour already in memory would feel
  // broken on a good link and be unusable on a vessel's.
  let remote: GazPlace[] = [];
  /** The folded query `remote` belongs to — replies that lose the race are
   *  dropped rather than shown against the wrong text. */
  let remoteFor = "";
  let remoteBusy = false;
  let remoteFailed = false;
  let remoteTimer: number | null = null;
  let remoteAbort: AbortController | null = null;
  /** Answered queries, kept for the session. Backspacing through a word retypes
   *  the same prefixes, and none of them should cost a second request. */
  const remoteCache = new Map<string, GazPlace[]>();

  const askRemote = (q: string): void => {
    if (remoteTimer !== null) window.clearTimeout(remoteTimer);
    remoteAbort?.abort();
    if (q.length < MIN_REMOTE_QUERY) {
      remote = [];
      remoteFor = q;
      remoteBusy = false;
      remoteFailed = false;
      return;
    }
    const hit = remoteCache.get(q);
    if (hit !== undefined) {
      remote = hit;
      remoteFor = q;
      remoteBusy = false;
      remoteFailed = false;
      return;
    }
    remoteBusy = true;
    remoteFailed = false;
    remoteTimer = window.setTimeout(() => {
      remoteTimer = null;
      const ac = new AbortController();
      remoteAbort = ac;
      void gazetteerSearch(q, ac.signal)
        .then((found) => {
          if (ac.signal.aborted) return;
          remoteCache.set(q, found);
          remote = found;
          remoteFor = q;
          remoteBusy = false;
          if (fold(input.value) === q) run(input.value);
        })
        .catch(() => {
          if (ac.signal.aborted) return;
          // Said, not swallowed. The local results are still right and still
          // shown; what the operator must not do is conclude from an empty list
          // that a sea does not exist when the truth is that we could not ask.
          remoteBusy = false;
          remoteFailed = true;
          if (fold(input.value) === q) run(input.value);
        });
    }, REMOTE_DEBOUNCE_MS);
  };

  let rows: Place[] = [];
  let active = -1;
  /** Whether the list is showing. Tracked because the status footer can be the
   *  only thing in it, and a closed box must stay closed. */
  let open_ = false;

  function candidates(raw: string): Place[] {
    const q = fold(raw);
    const out: { p: Place; score: number; nm: number }[] = [];

    // A typed position outranks everything: it is not a guess, it is an address.
    const at = parseCoords(raw);
    if (at !== null) {
      out.push({
        p: {
          kind: "position",
          name: fmtCoord(at.lat, at.lon),
          sub: "Go to this position",
          lat: at.lat,
          lon: at.lon,
        },
        score: -1,
        nm: 0,
      });
    }

    if (q.length >= MIN_QUERY) {
      const here = opts.here();
      const push = (p: Place, name: string): void => {
        const score = matchScore(name, q);
        if (score < 0) return;
        out.push({
          p,
          score,
          nm: here === null ? 0 : roughNm(here.lat, here.lon, p.lat, p.lon),
        });
      };

      for (const w of listSaved()) {
        push(
          { kind: "saved", name: w.name, sub: fmtCoord(w.lat, w.lon), lat: w.lat, lon: w.lon },
          w.name,
        );
      }
      for (const w of WAYPOINTS as readonly Waypoint[]) {
        push(
          { kind: "waypoint", name: w.name, sub: w.note, lat: w.lat, lon: w.lon, spanNm: w.spanNm },
          w.name,
        );
      }
      if (ports !== null) {
        for (const p of ports) {
          push(
            {
              kind: "port",
              name: p.name,
              // Country plus position: the country separates same-named harbours
              // in different nations, and the position separates the two Camerons
              // inside one.
              sub: p.home ? `${p.country} · fleet base` : p.country,
              lat: p.lat,
              lon: p.lon,
            },
            p.name,
          );
        }
      }
      // The gazetteer's answer to the query these rows were fetched for. Held
      // separately from the local index so a stale reply can never be merged in:
      // `remoteFor` is the exact text they belong to.
      if (remoteFor === q) {
        for (const g of remote) {
          push(
            {
              kind: g.kind,
              name: g.name,
              // The record's own classification, then where it is. "Abyssal
              // Plain · 24.043°N 92.651°W" tells a party chief both what kind of
              // thing was found and WHICH one, which is the whole difficulty
              // with a name like Sigsbee Basin.
              sub: `${g.placeType} · ${fmtCoord(g.lat, g.lon)}`,
              lat: g.lat,
              lon: g.lon,
              ...(g.spanNm === undefined ? {} : { spanNm: g.spanNm }),
            },
            g.name,
          );
        }
      }
    }

    out.sort(
      (a, b) =>
        a.score - b.score ||
        KIND_RANK[a.p.kind] - KIND_RANK[b.p.kind] ||
        a.nm - b.nm ||
        a.p.name.localeCompare(b.p.name),
    );
    return out.slice(0, MAX_ROWS).map((r) => r.p);
  }

  /** Whether the list should be on screen at all: any row, or anything worth
   *  saying about the search itself. */
  function footerText(): string | null {
    if (remoteBusy) return "Searching seas, features and zones\u2026";
    if (remoteFailed) return "Gazetteer unreachable \u2014 ports and waypoints only";
    return null;
  }

  function paint(): void {
    list.replaceChildren();
    const foot = open_ ? footerText() : null;
    if (rows.length === 0 && foot === null) {
      list.hidden = true;
      input.setAttribute("aria-expanded", "false");
      return;
    }
    rows.forEach((p, i) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "mapsearch-row" + (i === active ? " on" : "");
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", String(i === active));
      const main = document.createElement("span");
      main.className = "mapsearch-name";
      main.textContent = p.name;
      const kind = document.createElement("span");
      kind.className = `mapsearch-kind k-${p.kind}`;
      kind.textContent = KIND_LABEL[p.kind];
      const sub = document.createElement("span");
      sub.className = "mapsearch-sub";
      sub.textContent = p.sub;
      row.append(main, kind, sub);
      // mousedown, not click: the input's blur would close the list first.
      row.addEventListener("mousedown", (e) => {
        e.preventDefault();
        choose(i);
      });
      list.appendChild(row);
    });
    if (foot !== null) {
      const f = document.createElement("div");
      f.className = "mapsearch-foot";
      f.textContent = foot;
      list.appendChild(f);
    }
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
  }

  function run(raw: string): void {
    open_ = true;
    askRemote(fold(raw));
    rows = candidates(raw);
    active = rows.length > 0 ? 0 : -1;
    paint();
  }

  function close(): void {
    open_ = false;
    rows = [];
    active = -1;
    paint();
  }

  function choose(i: number): void {
    const p = rows[i];
    if (p === undefined) return;
    input.value = p.name;
    clear.hidden = false;
    close();
    input.blur();
    opts.onPick(p);
  }

  input.addEventListener("input", () => {
    const v = input.value;
    clear.hidden = v === "";
    if (v.trim().length >= MIN_QUERY) ensurePorts();
    if (v.trim() === "") {
      close();
      return;
    }
    run(v);
  });
  input.addEventListener("focus", () => {
    if (input.value.trim() !== "") run(input.value);
  });
  input.addEventListener("blur", () => {
    // A frame's grace so a click on a row is not cancelled by the blur.
    window.setTimeout(close, 120);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (rows.length === 0) return;
      e.preventDefault();
      active = (active + (e.key === "ArrowDown" ? 1 : rows.length - 1)) % rows.length;
      paint();
      return;
    }
    if (e.key === "Enter") {
      if (active >= 0) {
        e.preventDefault();
        choose(active);
      }
      return;
    }
    if (e.key === "Escape") {
      if (rows.length > 0) close();
      else input.value = "";
      clear.hidden = input.value === "";
    }
  });
  clear.addEventListener("click", () => {
    input.value = "";
    clear.hidden = true;
    close();
    input.focus();
  });

  return {
    root,
    destroy(): void {
      root.remove();
    },
  };
}
